import React from "react";
import {
  filterTaskInfo,
  getStartEndTime,
  SearchGlobalContext
} from "@/components/Modals/SearchGlobalModal/utils";
import throttle from "lodash/throttle";
import ListEmptyPlaceholder from "@jd/focus-desktop-comps/lib/es/ListEmptyPlaceholder";
import { List, Empty } from "antd";
import { LoadingOutlined } from "@/utils/antdIcon";
import GroupTitle from "@/components/Modals/SearchGlobalModal/GroupTitle";
import lodashTrim from "lodash/trim";
import {
  SearchGlobalParams,
  SearchGlobalRes,
  SearchWorkTypes,
  TaskInfo,
  SearchType
} from "@/services/SearchService";
import TaskPaneItem from "./TaskPaneItem";
import { ListItemClickFn } from "../../Tabs";
import noContent from "@/assets/img/search-nodata.png";
import { analysisLog } from "@/utils/logAnalytics";
import { timeSlot } from "../../ConditionSearch";

export default function TabPaneTask({ onClick }: { onClick?: ListItemClickFn }) {
  const {
    onSearchGlobal,
    keyword,
    t,
    setTabIndex,
    activeKey,
    taskStatus,
    taskDeadlineTime,
    taskPriorityType
  } = React.useContext(SearchGlobalContext);
  const [result, setResult] = React.useState<SearchGlobalRes | null>(null);
  const [loading, setLoading] = React.useState(false);
  const keywordRef = React.useRef<string>();
  keywordRef.current = keyword;
  const activeKeyRef = React.useRef<SearchType>();
  activeKeyRef.current = activeKey;
  const taskStatusRef = React.useRef<number[] | undefined>([]);
  taskStatusRef.current = taskStatus;
  const taskDeadlineTimeRef = React.useRef<timeSlot | undefined>(undefined);
  taskDeadlineTimeRef.current = taskDeadlineTime;
  const taskPriorityTypeRef = React.useRef<number[] | undefined>([]);
  taskPriorityTypeRef.current = taskPriorityType;

  const doSearch = async () => {
    if (activeKeyRef.current !== SearchType.Task) {
      return;
    }
    if (!keywordRef.current) {
      return [];
    }
    try {
      setLoading(true);
      const params: SearchGlobalParams = {
        keyword: keywordRef.current,
        searchType: SearchType.Task,
        pageNo: 1,
        pageSize: 20
      };
      const taskParams: SearchWorkTypes = {};
      if (taskStatusRef.current) {
        taskParams.taskStatus = taskStatusRef.current;
      }
      if (taskPriorityTypeRef.current) {
        taskParams.priorityType = taskPriorityTypeRef.current;
      }
      if (taskDeadlineTimeRef.current) {
        taskParams.beginTime = getStartEndTime(taskDeadlineTimeRef.current)?.beginTime || "";
        taskParams.endTime = getStartEndTime(taskDeadlineTimeRef.current)?.endTime || "";
      }

      params.bizWorkInfo = taskParams;
      const res = await onSearchGlobal(params);

      if (keywordRef.current) {
        setResult(res);
      }
      setLoading(false);
    } catch (e) {
      setResult({});
      setLoading(false);
    }
  };

  const doSearchThrottle = React.useRef(throttle(doSearch, 500)).current;

  React.useEffect(() => {
    if (!lodashTrim(keywordRef.current)) {
      setResult(null);
      return;
    }
    doSearchThrottle();
  }, [keyword, doSearchThrottle, activeKey, taskStatus, taskDeadlineTime, taskPriorityType]);

  if (!result || !result.works) {
    return <ListEmptyPlaceholder placeholder={t("search.emptyTask")} />;
  }
  const { works } = result;

  const isEmpty = works
    .map((work) => Object.values(work)[0])
    .every((item) => {
      return !item || item?.length === 0;
    });
  if (isEmpty) {
    return <Empty image={noContent} description={t("search.nodata")} />;
  }
  const worksData = filterTaskInfo(works);

  return (
    <div>
      {worksData.map(
        ({ displayName, data }: { data: TaskInfo[]; displayName: string }, idx: number) => {
          if (!data || data.length === 0) return null;
          return (
            <div key={idx}>
              <GroupTitle title={`任务-${displayName}`} />
              <List
                loading={
                  loading && {
                    indicator: <LoadingOutlined size={16} />
                  }
                }
                dataSource={data}
                renderItem={(item: any) => (
                  <TaskPaneItem
                    onClick={() => {
                      analysisLog("xtbg_home_search_1641454991771", "30");
                      if (onClick) {
                        onClick(SearchType.Task, item);
                      }
                    }}
                    info={item}
                  />
                )}
              />
            </div>
          );
        }
      )}
    </div>
  );
}
